import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Map as MapIcon, MapPin, X, ZoomIn, Crown, Wine, Utensils, Palette } from 'lucide-react';

const MAPS = [
  {
    id: 'main-floor',
    title: 'Main Floor — Queens Court',
    subtitle: 'Festival entrance · Main Bar · Main Sponsor (Suntory) · 11 booth zones',
    image: '/maps/main-floor-queens-court.png',
    highlights: ['Entrance & Main Bar', 'Main Sponsor: Suntory', 'Pernod · Campari · Proximo', 'Shaka Mex · La Piña · Reyes RTD', 'Aloha Cancer Project booth'],
  },
  {
    id: '3rd-walkway',
    title: '3rd Floor Walkway',
    subtitle: 'Path to Moani\'s VIP & 3rd Floor Golf — VIP & Food zones',
    image: '/maps/third-floor-walkway.png',
    highlights: ['VIP zones #13 & #15', '1311 Food booth #14', 'RNDC #16', 'Elevator access', 'Walkway to Golf area'],
  },
  {
    id: '3rd-golf',
    title: '3rd Floor Golf Zone',
    subtitle: 'Main Bar 20 · Kapena Power · Pitch Sports Bar · Ritual Union Art · Taqueria La Marea',
    image: '/maps/third-floor-golf.png',
    highlights: ['Main Bar #20', 'Kapena Power #19 · Pitch Power #18', 'Taqueria La Marea #23', 'Ritual Union Art #17', "Rad's Cosmo Art #24", 'Purchase Script / Tequila Station'],
  },
  {
    id: 'moanis-vip',
    title: "Moani\u2019s VIP Section",
    subtitle: 'Premium seating & service · RNDC & Johnson lounges',
    image: '/maps/moanis-vip.png',
    highlights: ['VIP Lounge — premium seating & service', 'RNDC #27 · Johnson #28', '6 lounge seating areas', 'Sanitation station'],
    vip: true,
  },
];

const EventMapPage = () => {
  const [zoomed, setZoomed] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a14] via-[#0a0a0a] to-black text-white" data-testid="event-map-page">
      <Helmet>
        <title>Event Map | Aloha Tequila & Arts Festival 2026</title>
        <meta name="description" content="Floor maps for the Aloha Tequila & Arts Festival on June 20, 2026 at International Market Place, Waikiki. Main Floor, 3rd Floor Walkway, Golf Zone, and Moani's VIP Section layouts." />
      </Helmet>

      {/* Header */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-orange-500/5 to-amber-500/10 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10 sm:pt-12 sm:pb-14">
          <Link
            to="/"
            data-testid="map-back-home"
            className="inline-flex items-center gap-2 text-white/60 hover:text-pink-400 text-sm font-medium mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to homepage
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-pink-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/30">
              <MapIcon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div className="inline-flex items-center gap-1.5 bg-pink-500/15 text-pink-300 border border-pink-500/30 rounded-full px-3 py-1 text-xs font-bold">
              <MapPin className="w-3 h-3" />
              International Market Place · Waikiki
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-4">
            <span className="bg-gradient-to-r from-pink-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">
              Festival Map
            </span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
            Find every booth, bar, VIP lounge, art vendor, and sanitation station for the 1st Annual
            Aloha Tequila &amp; Arts Festival on <strong className="text-white">Saturday, June 20, 2026</strong>.
            Tap any map to zoom in — bring it with you on event day.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
            <div className="inline-flex items-center gap-1.5 bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 rounded-full px-3 py-1.5 font-semibold">
              <Wine className="w-3 h-3" /> Tequila vendors
            </div>
            <div className="inline-flex items-center gap-1.5 bg-orange-500/10 text-orange-300 border border-orange-500/30 rounded-full px-3 py-1.5 font-semibold">
              <Utensils className="w-3 h-3" /> Food vendors
            </div>
            <div className="inline-flex items-center gap-1.5 bg-purple-500/10 text-purple-300 border border-purple-500/30 rounded-full px-3 py-1.5 font-semibold">
              <Palette className="w-3 h-3" /> Art vendors
            </div>
            <div className="inline-flex items-center gap-1.5 bg-amber-500/10 text-amber-300 border border-amber-500/30 rounded-full px-3 py-1.5 font-semibold">
              <Crown className="w-3 h-3" /> VIP areas
            </div>
          </div>
        </div>
      </div>

      {/* Maps Grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        {MAPS.map((m, idx) => (
          <section
            key={m.id}
            data-testid={`map-section-${m.id}`}
            className={`bg-white/[0.04] border rounded-3xl overflow-hidden ${
              m.vip ? 'border-amber-500/40 shadow-xl shadow-amber-500/5' : 'border-white/10'
            }`}
          >
            <div className="flex items-start gap-3 sm:gap-4 px-5 sm:px-7 pt-6 sm:pt-7 pb-5">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center shrink-0 text-white font-black text-base sm:text-lg shadow-lg ${
                m.vip
                  ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-black shadow-amber-500/30'
                  : 'bg-gradient-to-br from-pink-500 to-orange-500 shadow-pink-500/30'
              }`}>
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2 leading-tight">
                  {m.vip && <Crown className="w-5 h-5 text-amber-400 shrink-0" />}
                  {m.title}
                </h2>
                <p className="text-sm text-white/55 mt-1">{m.subtitle}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setZoomed(m)}
              data-testid={`map-zoom-${m.id}`}
              className="group relative block w-full bg-black/30 cursor-zoom-in"
              aria-label={`Zoom into ${m.title}`}
            >
              <img
                src={m.image}
                alt={`${m.title} floor map`}
                loading={idx === 0 ? 'eager' : 'lazy'}
                className="w-full h-auto block"
              />
              <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm text-white text-xs font-semibold rounded-full px-3 py-1.5 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity sm:opacity-90">
                <ZoomIn className="w-3.5 h-3.5" />
                Tap to zoom
              </div>
            </button>

            <div className="px-5 sm:px-7 py-5 border-t border-white/10">
              <div className="text-xs font-bold uppercase tracking-wider text-white/45 mb-2">Highlights</div>
              <div className="flex flex-wrap gap-2">
                {m.highlights.map((h) => (
                  <span
                    key={h}
                    className={`text-xs px-2.5 py-1 rounded-full border ${
                      m.vip
                        ? 'bg-amber-500/10 text-amber-200 border-amber-500/30'
                        : 'bg-white/5 text-white/75 border-white/10'
                    }`}
                  >
                    {h}
                  </span>
                ))}
              </div>
            </div>
          </section>
        ))}

        <div className="mt-8 bg-white/[0.04] border border-white/10 rounded-2xl p-5 sm:p-6 text-center">
          <p className="text-white/70 text-sm sm:text-base mb-2">
            <strong className="text-white">Save these to your phone</strong> before festival day so you can navigate offline.
          </p>
          <p className="text-white/45 text-xs">
            Layouts subject to last-minute changes. Refer to onsite signage and festival staff for any updates on event day.
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {zoomed && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-2 sm:p-6 overflow-auto"
          data-testid="map-lightbox"
          onClick={() => setZoomed(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Zoomed map: ${zoomed.title}`}
        >
          <button
            type="button"
            onClick={() => setZoomed(null)}
            data-testid="map-lightbox-close"
            className="fixed top-4 right-4 z-[101] w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            aria-label="Close zoomed map"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="fixed top-4 left-4 z-[101] bg-black/70 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2 text-white text-sm font-semibold max-w-[calc(100%-4rem)] truncate">
            {zoomed.title}
          </div>
          <img
            src={zoomed.image}
            alt={`${zoomed.title} — full resolution`}
            className="max-w-none w-full h-auto sm:w-auto sm:max-h-[90vh] sm:max-w-[95vw] rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default EventMapPage;
